/**
 * QuestionAnswer Component
 * 
 * This component handles answering GMB questions, including:
 * - Display of questions and existing answers
 * - Answer editor with AI suggestions
 * - Submission and tracking of answers
 * - Question prioritization and filtering
 */

import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { 
  MessageSquare,
  Send,
  ThumbsUp,
  Clock,
  User,
  AlertCircle,
  Search,
  Filter,
  Sparkles,
  CheckCircle,
  MapPin,
  Copy,
  Save
} from 'lucide-react'
import type { GmbQuestion, AnswerQuestionInput } from '@/types/gmb'

/**
 * Props interface for QuestionAnswer component
 */
export interface QuestionAnswerProps {
  /** List of questions to display */
  questions: GmbQuestion[]
  /** Callback when answer is submitted */
  onSubmit: (answer: AnswerQuestionInput) => Promise<void>
  /** Whether submission is in progress */
  isSubmitting?: boolean
  /** Enable AI answer suggestions */
  enableAISuggestions?: boolean
  /** Show only unanswered questions */
  showOnlyUnanswered?: boolean
}

/**
 * Individual question item props
 */
interface QuestionItemProps {
  question: GmbQuestion & { location?: any }
  onAnswer: (answer: string) => Promise<void>
  isSubmitting: boolean
  enableAISuggestions: boolean
  isSelected?: boolean
  onToggleSelect?: () => void
  draftAnswer?: string
  onDraftChange?: (text: string) => void
}

/**
 * Individual question component
 */
function QuestionItem({ 
  question, 
  onAnswer, 
  isSubmitting,
  enableAISuggestions,
  isSelected = false,
  onToggleSelect,
  draftAnswer = '',
  onDraftChange
}: QuestionItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [answerText, setAnswerText] = useState(draftAnswer)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!answerText.trim()) {
      setError('Please enter an answer')
      return
    }

    try {
      setError(null)
      await onAnswer(answerText.trim())
      setAnswerText('')
      setIsExpanded(false)
    } catch (err) {
      setError('Failed to submit answer')
    }
  }

  // Update answer text when draft changes
  useEffect(() => {
    if (draftAnswer !== answerText && !isExpanded) {
      setAnswerText(draftAnswer)
    }
  }, [draftAnswer])

  // Update draft when typing
  useEffect(() => {
    if (onDraftChange && isExpanded && answerText !== draftAnswer) {
      onDraftChange(answerText)
    }
  }, [answerText])

  return (
    <div className={`border rounded-lg p-4 space-y-3 hover:shadow-sm transition-shadow ${isSelected ? 'ring-2 ring-primary' : ''}`}>
      {/* Question Header */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          {/* Selection checkbox */}
          {!question.is_answered && onToggleSelect && (
            <div className="pt-0.5">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={onToggleSelect}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
            </div>
          )}
          
          <div className="flex-1 space-y-1">
            <p className="font-medium text-sm">{question.text}</p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {question.location && (
                <span className="flex items-center gap-1 font-medium text-primary">
                  <MapPin className="h-3 w-3" />
                  {question.location.location_name}
                </span>
              )}
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {question.author_name || 'Anonymous'}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {new Date(question.gmb_created_at).toLocaleDateString()}
              </span>
              {question.upvote_count > 0 && (
                <span className="flex items-center gap-1">
                  <ThumbsUp className="h-3 w-3" />
                  {question.upvote_count}
                </span>
              )}
            </div>
          </div>
          <Badge variant={question.is_answered ? 'secondary' : 'destructive'}>
            {question.is_answered ? 'Answered' : 'Needs Answer'}
          </Badge>
        </div>

        {/* Existing Answer */}
        {question.is_answered && question.answer_text && (
          <div className="mt-2 pl-4 border-l-2 border-green-200 bg-green-50 p-3 rounded">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Your Answer</span>
              {question.answered_at && (
                <span className="text-xs text-green-600">
                  • {new Date(question.answered_at).toLocaleDateString()}
                </span>
              )}
            </div>
            <p className="text-sm text-green-900">{question.answer_text}</p>
          </div>
        )}
      </div>

      {/* Answer Form */}
      {!question.is_answered && (
        <>
          {!isExpanded ? (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsExpanded(true)}
              className="w-full"
            >
              <MessageSquare className="h-4 w-4 mr-1" />
              Answer Question
            </Button>
          ) : (
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Your Answer</label>
                {enableAISuggestions && (
                  <Button variant="ghost" size="sm">
                    <Sparkles className="h-4 w-4 mr-1" />
                    AI Suggest
                  </Button>
                )}
              </div>
              
              <Textarea
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                placeholder="Provide a helpful answer..."
                rows={3}
                className="resize-none"
                disabled={isSubmitting}
              />
              
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setIsExpanded(false)
                    setAnswerText('')
                    setError(null)
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !answerText.trim()}
                >
                  <Send className="h-4 w-4 mr-1" />
                  Submit Answer
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Component for managing GMB Q&A
 */
export function QuestionAnswer({
  questions,
  onSubmit,
  isSubmitting = false,
  enableAISuggestions = true,
  showOnlyUnanswered = false
}: QuestionAnswerProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterUnanswered, setFilterUnanswered] = useState(showOnlyUnanswered)
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set())
  const [showBulkAnswer, setShowBulkAnswer] = useState(false)
  const [bulkAnswerText, setBulkAnswerText] = useState('')
  const [draftAnswers, setDraftAnswers] = useState<Map<string, string>>(new Map())

  // Filter questions
  const filteredQuestions = questions.filter(q => {
    const matchesSearch = q.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (q.author_name && q.author_name.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesFilter = !filterUnanswered || !q.is_answered
    return matchesSearch && matchesFilter
  })

  // Sort questions by priority
  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    // Unanswered first
    if (a.is_answered !== b.is_answered) {
      return a.is_answered ? 1 : -1
    }
    // Then by upvotes (popular first)
    if (a.upvote_count !== b.upvote_count) {
      return b.upvote_count - a.upvote_count
    }
    // Then by date (recent first)
    return new Date(b.gmb_created_at).getTime() - new Date(a.gmb_created_at).getTime()
  })

  // Stats
  const stats = {
    total: questions.length,
    answered: questions.filter(q => q.is_answered).length,
    unanswered: questions.filter(q => !q.is_answered).length,
    trending: questions.filter(q => q.upvote_count > 5).length
  }

  // Handle answer submission
  const handleAnswer = async (question: GmbQuestion, answerText: string) => {
    await onSubmit({
      gmb_location_id: question.gmb_location_id,
      gmb_question_id: question.gmb_question_id,
      answer_text: answerText
    })
    
    // Clear draft after submission
    const newDrafts = new Map(draftAnswers)
    newDrafts.delete(question.id)
    setDraftAnswers(newDrafts)
  }

  // Handle bulk answer submission
  const handleBulkAnswer = async () => {
    if (!bulkAnswerText.trim() || selectedQuestions.size === 0) return

    for (const questionId of selectedQuestions) {
      const question = questions.find(q => q.id === questionId)
      if (question && !question.is_answered) {
        await handleAnswer(question, bulkAnswerText.trim())
      }
    }

    // Clear bulk state
    setSelectedQuestions(new Set())
    setShowBulkAnswer(false)
    setBulkAnswerText('')
  }

  // Save draft answer
  const saveDraft = (questionId: string, text: string) => {
    const newDrafts = new Map(draftAnswers)
    if (text.trim()) {
      newDrafts.set(questionId, text)
    } else {
      newDrafts.delete(questionId)
    }
    setDraftAnswers(newDrafts)
  }

  // Toggle question selection
  const toggleSelection = (questionId: string) => {
    const newSelection = new Set(selectedQuestions)
    if (newSelection.has(questionId)) {
      newSelection.delete(questionId)
    } else {
      newSelection.add(questionId)
    }
    setSelectedQuestions(newSelection)
  }

  // Select all unanswered questions
  const selectAllUnanswered = () => {
    const unansweredIds = new Set(
      sortedQuestions
        .filter(q => !q.is_answered)
        .map(q => q.id)
    )
    setSelectedQuestions(unansweredIds)
  }

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Questions & Answers</CardTitle>
              <CardDescription>
                Manage and respond to customer questions from Google
              </CardDescription>
            </div>
            <Badge variant="outline">
              <MessageSquare className="h-3 w-3 mr-1" />
              GMB Q&A
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Total Questions</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.answered}</div>
              <p className="text-xs text-muted-foreground">Answered</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{stats.unanswered}</div>
              <p className="text-xs text-muted-foreground">Unanswered</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.trending}</div>
              <p className="text-xs text-muted-foreground">Popular</p>
            </div>
          </div>

          {/* Alert for unanswered questions */}
          {stats.unanswered > 0 && (
            <Alert className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You have {stats.unanswered} unanswered question{stats.unanswered !== 1 ? 's' : ''}.
                Answering questions helps improve your local search ranking.
              </AlertDescription>
            </Alert>
          )}

          {/* Search and Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant={filterUnanswered ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterUnanswered(!filterUnanswered)}
            >
              <Filter className="h-4 w-4 mr-1" />
              Unanswered Only
            </Button>
            {stats.unanswered > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllUnanswered}
              >
                <Copy className="h-4 w-4 mr-1" />
                Select All Unanswered
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk Answer Section */}
      {selectedQuestions.size > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Bulk Answer</CardTitle>
                <CardDescription>
                  Answer {selectedQuestions.size} selected questions with the same response
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedQuestions(new Set())
                  setShowBulkAnswer(false)
                }}
              >
                Clear Selection
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={bulkAnswerText}
              onChange={(e) => setBulkAnswerText(e.target.value)}
              placeholder="Enter an answer that will be used for all selected questions..."
              rows={4}
              className="bg-white"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedQuestions(new Set())
                  setBulkAnswerText('')
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkAnswer}
                disabled={!bulkAnswerText.trim() || isSubmitting}
              >
                <Copy className="h-4 w-4 mr-1" />
                Answer {selectedQuestions.size} Questions
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Questions List */}
      <div className="space-y-3">
        {sortedQuestions.length > 0 ? (
          sortedQuestions.map((question) => (
            <QuestionItem
              key={question.id}
              question={question}
              onAnswer={(answer) => handleAnswer(question, answer)}
              isSubmitting={isSubmitting}
              enableAISuggestions={enableAISuggestions}
              isSelected={selectedQuestions.has(question.id)}
              onToggleSelect={() => toggleSelection(question.id)}
              draftAnswer={draftAnswers.get(question.id) || ''}
              onDraftChange={(text) => saveDraft(question.id, text)}
            />
          ))
        ) : (
          <Card>
            <CardContent className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                {searchTerm || filterUnanswered 
                  ? 'No questions match your filters' 
                  : 'No questions yet'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

export default QuestionAnswer